/* iter-studio-dashboard — Prompt Quality Checklist widget.
 *
 * A compact "high-impact blanks" panel that fetches
 * `/api/prompt-analytics/me` and renders every tracked prompt field
 * with a fill-rate bar. Fields under 40% are highlighted so the
 * writer can see at a glance which levers they habitually skip.
 *
 * Drop it into any framework/dashboard surface — pure read-only.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ListChecks, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Friendlier labels for the tracked field keys.
const LABELS = {
  'character.name':        'Character · name',
  'character.role':        'Character · role',
  'character.voice':       'Character · voice',
  'character.want':        'Character · want',
  'character.wound':       'Character · wound',
  'character.description': 'Character · description',
  'character.traits':      'Character · traits',
  'story.mood':            'Story bible · mood',
  'story.themes':          'Story bible · themes',
  'framework.logline':     'Framework · logline',
  'framework.structure':   'Framework · act structure',
  'framework.chapters_count': 'Framework · chapter plan',
  'framework.variables':   'Framework · variables',
  'chapter.beat_outline':  'Chapter · beat outline',
  'chapter.setting':       'Chapter · setting',
  'chapter.conflict':      'Chapter · conflict',
  'chapter.revelation':    'Chapter · revelation',
};

export default function PromptQualityChecklist({ days = 30, testId = 'prompt-quality-checklist' }) {
  const [data, setData]     = useState(null);
  const [loading, setL]     = useState(true);

  useEffect(() => {
    let alive = true;
    setL(true);
    axios.get(`${API}/prompt-analytics/me`, { params: { days } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ total_prompts: 0, fields: [] }); })
      .finally(() => { if (alive) setL(false); });
    return () => { alive = false; };
  }, [days]);

  // iter-studio-dashboard — Clicking a weak field emits a global
  // `pw-focus-bible-field` event with the field key. Bible / Story
  // Bible panels can listen and scroll+highlight the matching input.
  // Also fires a toast so the writer sees something happened even if
  // the target panel isn't mounted yet.
  const focusField = (fieldKey) => {
    try {
      window.dispatchEvent(new CustomEvent('pw-focus-bible-field',
        { detail: { field: fieldKey } }));
    } catch { /* ignore */ }
    const label = LABELS[fieldKey] || fieldKey;
    const hint  = fieldKey.startsWith('character.')
      ? 'Open Character Bible → this field to fill it in.'
      : fieldKey.startsWith('story.')
      ? 'Open Story Bible → this field to fill it in.'
      : fieldKey.startsWith('framework.')
      ? 'Open the Framework page → this field to fill it in.'
      : 'Fill this field in the current chapter row.';
    toast.info(`${label} · ${hint}`, { duration: 4200 });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-card p-3 text-[11.5px] text-muted-foreground"
           data-testid={`${testId}-loading`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading prompt analytics…
      </div>
    );
  }

  const total = data?.total_prompts || 0;
  const rows  = data?.fields || [];
  const weakest = rows.slice().sort((a, b) => a.rate - b.rate).slice(0, 3);

  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
          <ListChecks className="h-3 w-3 text-primary" />
          Prompt Quality · last {data?.days || days} days
        </div>
        <span className="text-[10.5px] text-muted-foreground">
          {total} prompt{total === 1 ? '' : 's'} sampled
        </span>
      </div>

      {total === 0 ? (
        <p className="mt-2 text-[11.5px] text-muted-foreground"
           data-testid={`${testId}-empty`}>
          Draft a chapter with Claude to start seeing which bible + framework
          fields you fill in most (and which you routinely leave blank).
        </p>
      ) : (
        <>
          {weakest.length > 0 && weakest[0].rate < 40 && (
            <div
              className="mt-2 flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-900 dark:text-amber-100"
              data-testid={`${testId}-nudge`}
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
              <div>
                <div className="font-semibold">Fill these first — biggest quality lift:</div>
                <ul className="mt-0.5 list-disc pl-4">
                  {weakest.map((w) => (
                    <li key={w.field}>
                      {LABELS[w.field] || w.field}
                      <span className="ml-1 font-mono text-[10.5px] text-muted-foreground">
                        {w.rate}% filled
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <ul className="mt-3 space-y-1" data-testid={`${testId}-list`}>
            {rows.map((r) => {
              const tone = r.rate >= 80 ? 'bg-emerald-500'
                : r.rate >= 40 ? 'bg-amber-500'
                : 'bg-rose-500';
              return (
                <li key={r.field}
                    className="flex items-center gap-2"
                    data-testid={`${testId}-row-${r.field.replace(/\./g, '-')}`}>
                  {r.rate >= 80
                    ? <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600" />
                    : <span className="h-3 w-3 shrink-0" />}
                  <button
                    type="button"
                    onClick={() => focusField(r.field)}
                    className="flex-1 truncate text-left text-[11.5px] hover:text-primary hover:underline"
                    data-testid={`${testId}-row-btn-${r.field.replace(/\./g, '-')}`}
                    title="Jump to this field in the Bible / Framework"
                  >
                    {LABELS[r.field] || r.field}
                  </button>
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                    <div className={`h-full ${tone} transition-all`}
                         style={{ width: `${r.rate}%` }} />
                  </div>
                  <span className="w-9 text-right font-mono text-[10.5px] text-muted-foreground">
                    {r.rate}%
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
